<template>
  <div class="login-page-wrapper relative min-h-screen overflow-hidden">
    <!-- Dynamic Form Styling -->
    <component :is="'style'" v-if="dynamicTextStyles">{{
      dynamicTextStyles
    }}</component>

    <!-- Loading overlay -->
    <div
      v-if="!isBackgroundReady"
      class="fixed inset-0 bg-black z-50 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-4">
        <div
          class="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin"
        ></div>
        <p class="text-white/80 text-sm">Carregando...</p>
      </div>
    </div>

    <!-- Solid gradient background -->
    <div class="auth-background" :style="customBackgroundStyle"></div>

    <!-- Subtle overlay for depth -->
    <div class="auth-overlay"></div>

    <!-- Content layer -->
    <div
      class="login-glass relative z-10 min-h-screen flex flex-col px-6 py-10 sm:py-12 lg:px-0 lg:py-0"
    >
      <!-- TOP: Logo (mobile only) + Theme toggle -->
      <div
        class="flex items-center justify-between w-full max-w-sm mx-auto lg:max-w-none lg:justify-end lg:px-12 lg:pt-8 lg:pb-4"
      >
        <div class="lg:hidden">
          <img
            :src="logoUrl"
            :class="[
              'h-12 w-auto',
              { 'logo-invert-dark': isDnwaysOrBritech || isGenial },
            ]"
            :alt="nomeEmpresa"
          />
        </div>

        <div class="flex items-center gap-2">
          <BackgroundCustomizer
            ref="backgroundCustomizer"
            :nomeEmpresa="nomeEmpresa"
            :showControls="showBackgroundCustomizer"
            @background-change="handleBackgroundChange"
            @button-color-change="handleButtonColorChange"
            @form-styling-change="handleFormStyling"
          />

          <button
            @click="handleThemeClick"
            class="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Toggle theme"
            :aria-pressed="isDark"
          >
            <svg
              v-if="!isDark"
              class="w-5 h-5 text-yellow-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              />
            </svg>
            <svg
              v-else
              class="w-5 h-5 text-slate-300"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- CENTER: Main content -->
      <div
        class="flex-1 flex flex-col justify-center w-full"
        :class="parentContainerClass"
        :style="parentContainerStyle"
      >
        <div
          class="form-container"
          :class="[formContainerClass, childContainerWidthClass]"
          :style="formContainerStyle"
        >
          <!-- Logo com backdrop (desktop only) -->
          <div class="hidden lg:flex justify-center mb-10">
            <div
              :class="[
                'inline-flex items-center justify-center px-3 py-2 rounded-xl',
                { 'bg-black/10': !isFormStylingActive },
              ]"
            >
              <img
                :src="logoUrl"
                :class="[
                  'h-20 lg:h-24 w-auto',
                  {
                    'logo-invert-dark':
                      !isFormStylingActive && (isDnwaysOrBritech || isGenial),
                    'logo-invert': shouldInvertLogo,
                  },
                ]"
                :alt="nomeEmpresa"
              />
            </div>
          </div>

          <!-- Descrição -->
          <div class="flex justify-center mb-8">
            <span class="text-sm text-white/60 text-center">
              Defina uma nova senha para acessar sua conta
            </span>
          </div>

          <!-- Mensagem estática de token expirado/inválido -->
          <div v-if="tokenInvalido" class="space-y-4 text-center">
            <div class="text-sm text-white/80">
              Este link expirou ou já foi utilizado.
              <br />
              Solicite um novo link na tela de login.
            </div>
            <router-link
              to="/login"
              class="inline-block w-full text-center py-2.5 px-4 rounded-lg text-white font-medium bg-gray-500 hover:bg-gray-600 transition-colors"
            >
              Voltar para o login
            </router-link>
          </div>

          <!-- Formulário de senha -->
          <form v-else @submit.prevent="definirSenha" class="space-y-4">
            <div>
              <label class="block text-sm font-medium mb-2">Nova senha*</label>
              <Password
                v-model="cadastro.novaSenha"
                inputClass="w-full"
                :feedback="false"
                toggleMask
                placeholder="Digite a nova senha"
                autocomplete="new-password"
              />
            </div>

            <div>
              <label class="block text-sm font-medium mb-2">
                Confirmar nova senha*
              </label>
              <Password
                v-model="cadastro.confirmarNovaSenha"
                inputClass="w-full"
                :feedback="false"
                toggleMask
                placeholder="Confirme a nova senha"
                autocomplete="new-password"
              />
            </div>

            <Button
              type="submit"
              :loading="disableBtnLogin"
              :disabled="disableBtnLogin"
              label="Definir senha"
              class="w-full btn-dynamic-color"
              :style="
                customButtonColor
                  ? `background-color: ${customButtonColor} !important; border-color: ${customButtonColor} !important;`
                  : ''
              "
            />
          </form>

          <!-- Link voltar -->
          <div class="mt-6 text-center">
            <router-link
              to="/login"
              class="text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              Voltar para login
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/axios";
import SHA256 from "crypto-js/sha256";
import backgroundConfigService from "@/services/backgroundConfigService";
import BackgroundCustomizer from "@/components/BackgroundCustomizer.vue";
import Button from "primevue/button";
import Password from "primevue/password";

export default {
  name: "DefinirSenha",
  components: {
    BackgroundCustomizer,
    Button,
    Password,
  },
  data() {
    return {
      nomeEmpresa: "",
      guidCliente: "",
      cadastro: {
        novaSenha: "",
        confirmarNovaSenha: "",
      },
      token: "",
      tokenInvalido: false,
      disableBtnLogin: false,
      customBackgroundStyle: {
        background: "linear-gradient(135deg, #00235C, #003D82, #0056A8)",
      },
      customButtonColor: null,
      isBackgroundReady: false,
      formStylingConfig: null,
      isDark: true,
      showBackgroundCustomizer: false,
    };
  },
  computed: {
    isDnwaysOrBritech() {
      return (
        this.nomeEmpresa === "DNWAYS" ||
        this.nomeEmpresa === "BRITECH" ||
        this.nomeEmpresa === "DNWAYS_DEMO_Y" ||
        this.nomeEmpresa === "DNWAYS_DEMO_X"
      );
    },
    isGenial() {
      return this.nomeEmpresa === "GENIAL";
    },
    detectedDevice() {
      const width = window.innerWidth;
      if (width < 768) return "mobile";
      if (width < 1024) return "tablet";
      return "desktop";
    },
    logoUrl() {
      return require("@/" + process.env.VUE_APP_URL_LOGO);
    },
    isFormStylingActive() {
      return this.formStylingConfig?.enabled?.[this.detectedDevice] || false;
    },
    currentFormConfig() {
      if (!this.isFormStylingActive) return null;
      return this.formStylingConfig?.[this.detectedDevice] || null;
    },
    formContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { backgroundColor, opacity, width, minWidth, maxWidth, margin } =
        this.currentFormConfig;
      return {
        backgroundColor: this.hexToRgba(backgroundColor, opacity / 100),
        backdropFilter: "blur(16px)",
        borderRadius: "1rem",
        padding: "2rem",
        width: width || "100%",
        minWidth: minWidth || "0px",
        maxWidth: maxWidth || "600px",
        marginLeft: margin || "0rem",
        marginRight: margin || "0rem",
      };
    },
    dynamicTextStyles() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const config = this.currentFormConfig;
      return `
        .form-container h1, .form-container h2, .form-container h3,
        .form-container h4, .form-container h5, .form-container h6,
        .form-container span, .form-container label, .form-container p {
          color: ${config.textColor} !important;
        }
        .form-container input:enabled, .form-container select:enabled, .form-container textarea:enabled {
          background-color: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .form-container input:disabled, .form-container select:disabled, .form-container textarea:disabled {
          background-color: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container label,
        .login-glass .form-container p {
          color: ${config.textColor} !important;
          background: none !important;
        }
        .login-glass .form-container .p-inputtext:enabled,
        .login-glass .form-container input.p-inputtext:enabled,
        .login-glass .form-container input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-inputtext:enabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:enabled,
        .login-glass .form-container .p-dropdown:enabled,
        .login-glass .form-container select:enabled,
        .login-glass .form-container textarea:enabled {
          background: ${config.inputBgEnabled} !important;
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext:disabled,
        .login-glass .form-container input.p-inputtext:disabled,
        .login-glass .form-container input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-inputtext:disabled,
        .login-glass .form-container .p-password input.p-component.p-inputtext:disabled,
        .login-glass .form-container .p-dropdown:disabled,
        .login-glass .form-container select:disabled,
        .login-glass .form-container textarea:disabled {
          background: ${config.inputBgDisabled} !important;
        }
        .login-glass .form-container .p-inputtext,
        .login-glass .form-container input.p-inputtext,
        .login-glass .form-container .p-password input {
          color: ${config.inputTextColor} !important;
        }
        .login-glass .form-container .p-inputtext::placeholder,
        .login-glass .form-container input.p-inputtext::placeholder,
        .login-glass .form-container .p-password input::placeholder {
          color: ${config.inputTextColor} !important;
          opacity: 0.6 !important;
        }
        .form-container a,
        .form-container ~ div a,
        .form-container ~ .text-center a {
          color: ${config.textColor} !important;
        }
        .form-container button:not([type="submit"]),
        .form-container ~ button:not([type="submit"]),
        button.flex.items-center {
          color: ${config.textColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full,
        .form-container button.btn-dynamic-color,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined),
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) .p-button-label,
        .form-container .p-button:not(.p-button-text):not(.p-button-outlined) span {
          color: ${config.buttonTextColor} !important;
        }
        .form-container button[type="submit"],
        .form-container button.w-full:not(.border),
        .form-container button.btn-dynamic-color,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined),
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined) {
          background-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
          border-color: ${this.hexToRgba(
            config.buttonColorType1,
            config.buttonOpacityType1 / 100,
          )} !important;
        }
        .form-container button[type="submit"]:hover,
        .form-container button.w-full:not(.border):hover,
        .form-container button.btn-dynamic-color:hover,
        .login-glass .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .login-glass .form-container .p-button:not(.p-button-text):not(.p-button-outlined):hover,
        .p-button:not(.p-button-text):not(.p-button-outlined):hover .p-button-label {
          color: ${config.buttonHoverColor} !important;
        }
        button.w-full.border,
        button.h-12.border,
        .landing-cta button.border {
          background-color: ${this.hexToRgba(
            config.buttonColorType2,
            config.buttonOpacityType2 / 100,
          )} !important;
          border-color: ${config.buttonColorType2} !important;
        }
      `;
    },
    formContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return "";
      const { alignment } = this.currentFormConfig;
      return alignment === "left"
        ? "mr-auto"
        : alignment === "right"
        ? "ml-auto"
        : "mx-auto";
    },
    parentContainerClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "items-center mx-auto";
      }
      const { alignment } = this.currentFormConfig;
      if (alignment === "left") {
        return "items-start";
      } else if (alignment === "right") {
        return "items-end";
      } else {
        return "items-center mx-auto";
      }
    },
    parentContainerStyle() {
      if (!this.isFormStylingActive || !this.currentFormConfig) return {};
      const { alignment, alignmentOffset } = this.currentFormConfig;
      const style = {};
      if (alignment === "left") {
        style.paddingLeft = `${alignmentOffset * 0.25}rem`;
      } else if (alignment === "right") {
        style.paddingRight = `${alignmentOffset * 0.25}rem`;
      }
      return style;
    },
    childContainerWidthClass() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return "w-full";
      }
      return "";
    },
    shouldInvertLogo() {
      if (!this.isFormStylingActive || !this.currentFormConfig) {
        return this.isDnwaysOrBritech || this.isGenial;
      }
      return this.currentFormConfig.logoInvert;
    },
  },
  async mounted() {
    this.nomeEmpresa = process.env.VUE_APP_NOME_EMPRESA;
    this.guidCliente = this.$route.params.id;
    this.token = this.$route.query.token || "";
    this.disableBtnLogin = false;

    // Carrega a configuração de estilo do form diretamente do serviço.
    // O <BackgroundCustomizer> só executa seu próprio loadConfig quando
    // showControls=true (template usa v-if na raiz), então precisamos
    // buscar a config aqui para garantir que o styling seja aplicado.
    try {
      const config = await backgroundConfigService.loadConfig();
      if (config) {
        // O loadConfig() retorna { selectedBgColor, ..., formStyling: {...} }.
        // O handler espera o mesmo formato que o BackgroundCustomizer emite via
        // @form-styling-change, que é o próprio objeto formStyling.
        this.handleFormStyling(config.formStyling || config);
      }
    } catch (error) {
      console.warn("Não foi possível carregar configuração de estilo:", error);
    }
  },
  methods: {
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    handleFormStyling(config) {
      this.formStylingConfig = config;
    },
    handleBackgroundChange(style) {
      this.customBackgroundStyle = style;
      this.isBackgroundReady = true;
    },
    handleButtonColorChange(color) {
      this.customButtonColor = color;
    },
    handleThemeClick() {
      this.isDark = !this.isDark;
      if (this.$refs.backgroundCustomizer) {
        const newMode = this.isDark ? "dark" : "light";
        this.$refs.backgroundCustomizer.syncModeWithTheme(newMode);
      }
    },
    validarForm() {
      if (!this.cadastro.novaSenha || this.cadastro.novaSenha.length < 6) {
        this.$swal({
          icon: "error",
          title: "Definição de senha",
          text: "A nova senha deve ter ao menos 6 caracteres.",
        });
        return false;
      }
      if (
        !this.cadastro.confirmarNovaSenha ||
        this.cadastro.novaSenha !== this.cadastro.confirmarNovaSenha
      ) {
        this.$swal({
          icon: "error",
          title: "Definição de senha",
          text: "A confirmação deve ser igual à nova senha.",
        });
        return false;
      }
      return true;
    },
    async definirSenha() {
      if (!this.guidCliente) {
        this.$swal({
          icon: "error",
          title: "Definição de senha",
          text: "Identificador do cliente não encontrado.",
        });
        return;
      }

      if (!this.token) {
        this.tokenInvalido = true;
        return;
      }

      if (!this.validarForm()) {
        return;
      }

      this.disableBtnLogin = true;

      const novaSenhaHash = SHA256(this.cadastro.novaSenha).toString();
      const confirmarNovaSenhaHash = SHA256(
        this.cadastro.confirmarNovaSenha,
      ).toString();

      const model = {
        ClienteGuid: this.guidCliente,
        NovaSenha: novaSenhaHash,
        ConfirmarNovaSenha: confirmarNovaSenhaHash,
        Token: this.token,
      };

      try {
        const retorno = await api.post("/register/definir-senha", model);
        this.disableBtnLogin = false;

        if (retorno.status === 200) {
          this.$swal({
            icon: "success",
            title: "Senha definida!",
            text: "Sua senha foi atualizada com sucesso. Faça o login para continuar.",
          }).then(() => {
            this.$router.replace({ path: "/login" });
          });
        }
      } catch (error) {
        this.disableBtnLogin = false;
        const descricao =
          error?.response?.data?.descricao ||
          "Não foi possível definir a senha. Tente novamente.";
        // Token expirado, já usado ou substituído — marca estado estático.
        if (
          typeof descricao === "string" &&
          (descricao.toLowerCase().includes("token") ||
            descricao.toLowerCase().includes("link"))
        ) {
          this.tokenInvalido = true;
          return;
        }
        this.$swal({
          icon: "error",
          title: "Definição de senha",
          text: descricao,
        });
      }
    },
  },
};
</script>

<style scoped>
.toggle-password-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.25rem;
  height: 1.25rem;
  background: transparent;
  border: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23999'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'/%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-size: contain;
}

.toggle-password-btn.senha-visivel {
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23999'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'/%3e%3c/svg%3e");
}
</style>
